import { API_BASE_URL } from './api';
import { client } from './api/client.gen';
import {
  completeAgencyEnrollmentTaskClinicsEnrollmentsRecordIdTasksTaskIdCompletePost,
  duplicateClinicInsurancesClinicsInsuranceDuplicatePost,
  getAgencyClinicScopeAgencyClinicsScopeGet,
  getAgencyEnrollmentCountClinicsEnrollmentsCountGet,
  getAgencyEnrollmentsClinicsEnrollmentsGet,
  getAgencyPayerEnrollmentsClinicsEnrollmentsPayersGet,
  refreshAgencyEnrollmentsClinicsEnrollmentsRefreshPost,
} from './api/sdk.gen';
import type {
  BulkEnrollItem,
  BulkEnrollResponse,
  BulkEnrollResult,
  BulkEnrollReview,
  ClinicEnrollmentsResponse,
  PayerEnrollmentsResponse,
  RefreshAgencyEnrollmentsResponse,
  DuplicateInsurancesResponse,
  SyncClinicInfoResponse,
} from './api/types.gen';
import { getUserFacingApiMessage } from '../utils/apiErrorDetail';

// PayorType is generated from the backend enum — re-exported so consumers have a
// single import site. Display labels are keyed by the generated type, so a new
// backend value forces a compile error here until it gets a label.
export type { PayorType } from './api/types.gen';
import type {
  BillType,
  ChargeType,
  PayorType,
  TypeOfBill,
} from './api/types.gen';
import type { ReadinessSkipFlags } from '../constants/insuranceBilling';

export type BillingFrequency =
  'MONTHLY' | 'EPISODIC' | 'WEEKLY' | 'SEMIMONTHLY';
// Alias the generated enum rather than duplicating it — a hand-written copy
// silently drifted when CONSTANT was added.
export type { ChargeType };
// UB-04 FL 4 scheme (EMR-880); null = default 329 worksheet behavior.
export type { BillType, TypeOfBill };

export const PAYOR_TYPE_LABELS: Record<PayorType, string> = {
  MEDICARE_TRADITIONAL: 'Medicare Traditional',
  MEDICARE_ADVANTAGE: 'Medicare (Advantage/PFFS)',
  MEDICARE_HMO: 'Medicare (HMO/Per Visit)',
  MEDICAID_TRADITIONAL: 'Medicaid Traditional',
  MEDICAID_MANAGED_CARE: 'Medicaid (HMO/Managed Care)',
  PRIVATE: 'Private Insurance',
  PRIVATE_MANAGED_CARE: 'Private HMO/Managed Care',
  SELF_PAY: 'Self-Pay',
  WORKERS_COMP: "Worker's Compensation",
  TRICARE: 'TRICARE',
  VA_PLAN: 'Veteran Administration Plan',
  OTHER_GOVERNMENT: 'Other Government',
  TITLE_PROGRAMS: 'Title Programs',
  NONE_NO_CHARGE: 'None - No Charge for Current Service',
  UNKNOWN: 'Unknown',
};

/** Combobox options derived from PAYOR_TYPE_LABELS — one shared list for filters/forms. */
export const PAYOR_TYPE_OPTIONS: { value: PayorType; label: string }[] = (
  Object.entries(PAYOR_TYPE_LABELS) as [PayorType, string][]
).map(([value, label]) => ({ value, label }));

export type PayorEnrollmentStatus =
  'not_required' | 'in_progress' | 'failed' | 'complete';

export interface AgencyInsuranceResponse extends Partial<ReadinessSkipFlags> {
  id: string;
  name: string;
  agency_id: string;
  payor_id?: string;
  agency_clinic_id?: string | null;
  edi_payer_id: string;
  edi_clearinghouse: string;
  clinic_key: string;
  clinic_name: string;
  insurance_key: string;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
  include_location_codes_on_noa?: boolean | null;
  noa_omit_admitting_diagnosis?: boolean | null;
  payor_noa_include_location_codes?: boolean | null;
  payor_noa_omit_admitting_diagnosis?: boolean | null;
  bill_type?: BillType;
  include_non_covered_visits_on_claims?: boolean;
  payor_type?: PayorType | null;
  display_in_auth_manager?: boolean;
  stedi_eligibility_enabled?: boolean | null;
  eligibility_enrollment_status?: PayorEnrollmentStatus | null;
  institutional_claim_enabled?: boolean | null;
  institutional_claim_enrollment_status?: PayorEnrollmentStatus | null;
  era_enabled?: boolean | null;
  era_enrollment_status?: PayorEnrollmentStatus | null;
  mapped_electronic_payor_id?: string | null;
  retired_in_wellsky?: boolean;
  billing_settings_locked?: boolean;
  billing_frequency: BillingFrequency;
  pdgm_enabled: boolean;
  charge_type: ChargeType;
  type_of_bill?: TypeOfBill | null;
  do_not_multiply_charges_by_units: boolean;
  health_plan_id?: string | null;
  provider_code?: string | null;
  provider_number?: string | null;
  address_one?: string | null;
  address_two?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  created_at: string;
  updated_at: string;
}

// Re-export from generated types — avoid manual interface duplication
import type {
  AgencyClinicResponse,
  AgencyClinicCreate,
  AgencyClinicUpdate,
  AgencyClinicScopeResponse,
} from './api/types.gen';

export type {
  AgencyClinicResponse,
  AgencyClinicCreate,
  AgencyClinicUpdate,
  AgencyClinicScopeResponse,
};

/**
 * Get agency insurance records
 */
export const getAgencyInsurances = async (
  agencyId?: string,
  agencyClinicId?: string,
  skip?: number,
  limit?: number
): Promise<AgencyInsuranceResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  if (agencyClinicId) {
    params.append('agency_clinic_id', agencyClinicId);
  }
  if (skip !== undefined) {
    params.append('skip', skip.toString());
  }
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }

  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch agency insurances: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get agency clinics
 */
export const getAgencyClinics = async (
  agencyId?: string,
  skip: number = 0,
  limit: number = 100
): Promise<AgencyClinicResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());

  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinics: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get whether the caller can read every clinic in the agency.
 */
export const getAgencyClinicScope =
  async (): Promise<AgencyClinicScopeResponse> => {
    const { data } = await getAgencyClinicScopeAgencyClinicsScopeGet({
      throwOnError: true,
    });
    return data;
  };

/**
 * Get a specific agency clinic by ID
 */
export const getAgencyClinic = async (
  clinicId: string
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new agency clinic
 */
export const createAgencyClinic = async (
  clinicData: AgencyClinicCreate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an agency clinic
 */
export const updateAgencyClinic = async (
  clinicId: string,
  clinicData: AgencyClinicUpdate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to update agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Give up Kinnser/WellSky participation for one clinic (super-user only).
 *
 * Roger takes over the clinic's payers and the clinic-info sync stops writing
 * to it. The clinic key is kept, so participation can be restored.
 */
export const disconnectClinicFromKinnser = async (
  clinicId: string
): Promise<AgencyClinicResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/${clinicId}/kinnser-disconnect`,
    {
      method: 'POST',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to disconnect clinic from WellSky sync: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Restore Kinnser/WellSky participation for a disconnected clinic (super-user only).
 */
export const reconnectClinicToKinnser = async (
  clinicId: string
): Promise<AgencyClinicResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/${clinicId}/kinnser-reconnect`,
    {
      method: 'POST',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to reconnect clinic to WellSky sync: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Delete an agency clinic
 */
export const deleteAgencyClinic = async (clinicId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete agency clinic: ${response.statusText}`);
  }
};

export interface AgencyInsuranceCreateRequest extends Partial<ReadinessSkipFlags> {
  agency_clinic_id: string;
  name: string;
  edi_payer_id?: string;
  edi_clearinghouse?: string;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
  include_location_codes_on_noa?: boolean | null;
  noa_omit_admitting_diagnosis?: boolean | null;
  bill_type?: BillType;
  include_non_covered_visits_on_claims?: boolean;
  payor_type: PayorType;
  billing_frequency?: BillingFrequency;
  pdgm_enabled?: boolean;
  charge_type?: ChargeType;
  type_of_bill?: TypeOfBill | null;
  do_not_multiply_charges_by_units?: boolean;
  health_plan_id?: string | null;
  provider_code?: string | null;
  provider_number?: string | null;
  address_one?: string | null;
  address_two?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}

export interface AgencyInsuranceUpdateRequest extends Partial<ReadinessSkipFlags> {
  name?: string;
  edi_payer_id?: string | null;
  edi_clearinghouse?: string | null;
  visit_authorization_required?: boolean;
  noa_required?: boolean;
  include_location_codes_on_noa?: boolean | null;
  noa_omit_admitting_diagnosis?: boolean | null;
  bill_type?: BillType;
  include_non_covered_visits_on_claims?: boolean;
  payor_type?: PayorType | null;
  billing_frequency?: BillingFrequency | null;
  pdgm_enabled?: boolean | null;
  charge_type?: ChargeType | null;
  type_of_bill?: TypeOfBill | null;
  do_not_multiply_charges_by_units?: boolean | null;
  health_plan_id?: string | null;
  provider_code?: string | null;
  provider_number?: string | null;
  address_one?: string | null;
  address_two?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}

export interface PayorSearchResult {
  id: string;
  payor_name: string;
  electronic_payor_id?: string | null;
  clearinghouse_payor_id?: string | null;
  stedi_display_name?: string | null;
  noa_include_location_codes?: boolean | null;
  noa_omit_admitting_diagnosis?: boolean | null;
}

/**
 * Create a new agency insurance record (Roger EMR only)
 */
export const createAgencyInsurance = async (
  data: AgencyInsuranceCreateRequest
): Promise<AgencyInsuranceResponse> => {
  const response = await fetch(`${API_BASE_URL}/clinics/insurance`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      getUserFacingApiMessage(
        { response: { status: response.status, data: err } },
        'Failed to create insurance. Please try again.'
      )
    );
  }

  return response.json();
};

/**
 * Update an existing agency insurance record (Roger EMR only)
 */
export const updateAgencyInsurance = async (
  insuranceId: string,
  data: AgencyInsuranceUpdateRequest
): Promise<AgencyInsuranceResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/${insuranceId}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    const failure = { response: { status: response.status, data: err } };
    // Carry the response so callers can use getBusinessErrorMessage (4xx only).
    throw Object.assign(
      new Error(
        getUserFacingApiMessage(
          failure,
          'Failed to update insurance. Please try again.'
        )
      ),
      failure
    );
  }

  return response.json();
};

/**
 * Delete an agency insurance record (Roger EMR only)
 */
export const deleteAgencyInsurance = async (
  insuranceId: string
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/${insuranceId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      getUserFacingApiMessage(
        { response: { status: response.status, data: err } },
        'Failed to delete insurance. Please try again.'
      )
    );
  }
};

export type DuplicateClinicInsurancesResponse = DuplicateInsurancesResponse;

/**
 * Duplicate all of a source clinic's insurances onto a destination clinic.
 * Skips creating insurances whose Payer ID and name already exist on the
 * destination clinic, but still fills visit rates onto those matches when
 * they have no rates yet.
 */
export const duplicateClinicInsurances = async (
  sourceAgencyClinicId: string,
  destinationAgencyClinicId: string
): Promise<DuplicateClinicInsurancesResponse> => {
  try {
    const { data } =
      await duplicateClinicInsurancesClinicsInsuranceDuplicatePost({
        body: {
          source_agency_clinic_id: sourceAgencyClinicId,
          destination_agency_clinic_id: destinationAgencyClinicId,
        },
        throwOnError: true,
      });
    return data;
  } catch (error) {
    throw new Error(
      getUserFacingApiMessage(
        error,
        'Failed to duplicate insurances. Please try again.'
      )
    );
  }
};

/**
 * Search payors from the Stedi payor database
 */
export const searchPayors = async (
  query: string,
  limit: number = 20
): Promise<PayorSearchResult[]> => {
  const params = new URLSearchParams({
    search_term: query,
    limit: limit.toString(),
  });
  const response = await fetch(
    `${API_BASE_URL}/payors/search?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to search payors: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Sync clinics and insurances from clinic information by fetching from RogerDoc API
 */
export const syncClinicsFromClinicInfo =
  async (): Promise<SyncClinicInfoResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/agency-clinics/sync-from-clinic-info`,
      {
        method: 'POST',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to sync clinics: ${response.statusText}`);
    }

    return response.json();
  };

// Human-readable aliases for the generated per-insurance visit-rates client
// functions (the generated names embed the full route path).
export {
  getInsuranceVisitRateDefaultsClinicsInsuranceInsuranceIdRatesDefaultsGet as getInsuranceVisitRateDefaults,
  getInsuranceVisitRatesClinicsInsuranceInsuranceIdRatesGet as getInsuranceVisitRates,
  replaceInsuranceVisitRatesClinicsInsuranceInsuranceIdRatesPut as replaceInsuranceVisitRates,
} from './api/sdk.gen';

/**
 * Stedi enrollment state for the whole agency, one row per enrollment.
 *
 * Types and transport come from the generated client, so the 401 handling and
 * error shaping every other call gets applies here too.
 */
export type {
  BulkEnrollCandidate,
  BulkEnrollClinicGap,
  BulkEnrollItem,
  BulkEnrollResult,
  BulkEnrollReview,
  ClinicEnrollmentRow,
  ClinicEnrollmentsResponse,
  ClinicEnrollmentTask,
  ClinicEnrollmentTaskField,
  ClinicEnrollmentTaskLink,
  EnrollmentDisplayStatus,
  EnrollmentSupport,
  PayerClinicLine,
  PayerEnrollmentGroup,
  PayerEnrollmentsResponse,
  RefreshAgencyEnrollmentsResponse,
  UnlinkedInsurance,
} from './api/types.gen';

export type EnrollmentTransactionType =
  'institutionalClaimSubmission' | 'eligibilityCheck' | 'claimPayment';

export const getAgencyEnrollments =
  async (): Promise<ClinicEnrollmentsResponse> => {
    const response = await getAgencyEnrollmentsClinicsEnrollmentsGet({
      throwOnError: true,
    });
    return response.data;
  };

export const getAgencyEnrollmentCount = async (): Promise<number> => {
  const response = await getAgencyEnrollmentCountClinicsEnrollmentsCountGet({
    throwOnError: true,
  });
  return response.data.count;
};

export const refreshAgencyEnrollments =
  async (): Promise<RefreshAgencyEnrollmentsResponse> => {
    const response =
      await refreshAgencyEnrollmentsClinicsEnrollmentsRefreshPost({
        throwOnError: true,
      });
    return response.data;
  };

/**
 * Send the answers to one Stedi enrollment task (an NGS packet ID, say).
 *
 * Errors carry only the server's safe message.
 */
export const completeEnrollmentTask = async (
  recordId: string,
  taskId: string,
  values: Record<string, string>
): Promise<void> => {
  try {
    await completeAgencyEnrollmentTaskClinicsEnrollmentsRecordIdTasksTaskIdCompletePost(
      {
        path: { record_id: recordId, task_id: taskId },
        body: { values },
        throwOnError: true,
      }
    );
  } catch (error) {
    throw new Error(
      getUserFacingApiMessage(
        error,
        "Couldn't send this step. Please try again."
      )
    );
  }
};

/**
 * The same enrollments as getAgencyEnrollments, one row per payer.
 */
export const getAgencyPayerEnrollments =
  async (): Promise<PayerEnrollmentsResponse> => {
    const response = await getAgencyPayerEnrollmentsClinicsEnrollmentsPayersGet(
      {
        throwOnError: true,
      }
    );
    return response.data;
  };

/**
 * What Start enrollments would submit for these clinics (all, when empty).
 * Roger staff only.
 */
export const getBulkEnrollReview = async (
  clinicIds: string[]
): Promise<BulkEnrollReview> => {
  const response = await client.get<{ 200: BulkEnrollReview }, unknown, true>({
    url: '/clinics/enrollments/bulk',
    query: clinicIds.length ? { clinic_ids: clinicIds } : undefined,
    throwOnError: true,
  });
  return response.data;
};

/** Create the chosen enrollments in Stedi; each line reports its outcome. */
export const submitBulkEnrollments = async (
  items: BulkEnrollItem[],
  contactEmail: string
): Promise<BulkEnrollResult[]> => {
  const response = await client.post<
    { 200: BulkEnrollResponse },
    unknown,
    true
  >({
    url: '/clinics/enrollments/bulk',
    body: { items, contact_email: contactEmail },
    throwOnError: true,
  });
  return response.data.results ?? [];
};
